import React, { useState, useCallback, useEffect } from 'react';
import { ArrowRight, Play, ShoppingBag, Minus, Plus, Sparkles, Star, TrendingUp, Package } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import configs from '../config/config';
import { useCart } from '../context/CartContext';

const HeroSection = ({ config }) => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const {
    addToCart,
    changeQuantity,
    isInCart,
    getQuantity,
  } = useCart();

  const defaultConfig = {
    preset: 'split-layout',
    title: "Discover Your Perfect Style",
    subtitle: "Explore our latest collection of premium fashion pieces crafted for the modern individual.",
    badge: "New Collection 2025",
    ctaPrimary: { text: "Shop Now", link: "/shop" },
    ctaSecondary: { text: "View Details", link: "#" },
    stats: [
      { value: "500+", label: "Products" },
      { value: "50k+", label: "Happy Customers" },
      { value: "4.9", label: "Avg Rating" },
    ],
    product: null,
    // NEW: products array for product-grid preset
    products: [],
    devMode: false,
    customStyles: {
      container: {
        padding: 'py-12 md:py-20',
        background: 'bg-white',
        heroImage: '',
        backgroundImage: '',
        titleColor: '#111827',
        subtitleColor: '#4B5563',
        badgeBg: '#f3f4f6',
        badgeText: '#111827',
        ctaPrimaryBg: '#111827',
        ctaPrimaryText: '#ffffff',
        ctaSecondaryBorder: '#6b7280',
        ctaSecondaryText: '#374151',
      },
    },
  };

  const finalConfig = { ...defaultConfig, ...config };
  const {
    preset,
    title,
    subtitle,
    badge,
    ctaPrimary,
    ctaSecondary,
    stats,
    product,
    products,
    devMode,
    customStyles,
  } = finalConfig;

  const containerClasses = [];
  const containerStyle = {};

  const bgValue = customStyles.container?.background || 'bg-white';
  const bgImage = customStyles.container?.backgroundImage;

  if (bgValue.startsWith('bg-')) {
    containerClasses.push(bgValue);
  } else {
    containerStyle.background = bgValue;
  }

  if (bgImage) {
    if (bgImage.match(/\.(mp4|webm|mov)$/i)) {
      containerStyle.background = 'black';
    } else {
      containerStyle.backgroundImage = `url(${bgImage})`;
      containerStyle.backgroundSize = 'cover';
      containerStyle.backgroundPosition = 'center';
      containerStyle.backgroundRepeat = 'no-repeat';
    }
  }

  if (customStyles.container?.padding) {
    containerClasses.push(customStyles.container.padding);
  }

  const finalContainerClass = containerClasses.join(' ');

  const [wishlist, setWishlist] = useState([]);
  const toggleWishlist = (id) => {
    setWishlist((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const renderHeroMedia = (src, alt = "Hero media", className = "") => {
    if (!src) return null;
    const isVideo = src.match(/\.(mp4|webm|mov)$/i);
    if (isVideo) {
      return (
        <video
          src={src}
          autoPlay
          loop
          muted
          playsInline
          className={`w-full h-full object-cover ${className}`}
        />
      );
    }
    return (
      <img
        src={src}
        alt={alt}
        className={`w-full h-full object-cover ${className}`}
        onError={(e) => {
          e.target.src = 'https://placehold.co/600x600?text=Image+Failed';
        }}
      />
    );
  };

  // ─── Shared Add-to-Cart Button ──────────────────────────────────────────────
  const CartButton = ({ prod, primaryBg, primaryText, label }) => {
    if (!prod?.id) return null;

    if (!isInCart(prod.id)) {
      return (
        <button
          onClick={(e) => { e.stopPropagation(); addToCart(prod, 1); }}
          className="w-full inline-flex items-center justify-center px-6 py-4 rounded-full font-bold text-base shadow-xl transition-transform hover:scale-105"
          style={{
            backgroundColor: primaryBg || '#111827',
            color: primaryText || '#ffffff',
          }}
        >
          <ShoppingBag className="mr-2 w-5 h-5" />
          {label || "Add to Cart"}
        </button>
      );
    }

    return (
      <div className="w-full inline-flex items-center justify-center px-6 py-4 rounded-full font-bold text-base shadow-xl bg-gray-900 text-white">
        <button
          onClick={(e) => { e.stopPropagation(); changeQuantity(prod.id, -1); }}
          disabled={getQuantity(prod.id) <= 1}
          className="p-1.5 disabled:opacity-50"
        >
          <Minus className="w-4 h-4" />
        </button>
        <span className="w-10 text-center text-lg">{getQuantity(prod.id)}</span>
        <button
          onClick={(e) => { e.stopPropagation(); changeQuantity(prod.id, 1); }}
          className="p-1.5"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
    );
  };

  // ─── SPLIT LAYOUT ───────────────────────────────────────────────────────────
  // ─── SPLIT LAYOUT — Pinterest-style ────────────────────────────────────────
// Drop-in replacement for the SplitLayout component inside HeroSection.jsx
// The image panel is full-bleed (no padding, no rounding) filling the entire
// left half of the viewport height. Content sits flush-right with generous
// vertical centering.

const SplitLayout = () => (
  <div
    className="relative overflow-hidden w-full"
    style={{
      minHeight: '100vh',
      ...containerStyle,
      // Reset any background since left panel IS the visual
      background: customStyles.container?.background?.startsWith('bg-')
        ? undefined
        : containerStyle.background,
    }}
  >
    {/* Video background (whole section) if set */}
    {customStyles.container?.backgroundImage?.match(/\.(mp4|webm|mov)$/i) && (
      <video
        src={customStyles.container.backgroundImage}
        autoPlay loop muted playsInline
        className="absolute inset-0 w-full h-full object-cover -z-10"
      />
    )}

    <div className="flex flex-col lg:flex-row min-h-screen">

      {/* ── LEFT: Full-bleed image panel ───────────────────────────────── */}
      <div className="relative w-full lg:w-1/2 h-72 sm:h-96 lg:h-auto lg:min-h-screen overflow-hidden flex-shrink-0">
        {renderHeroMedia(
          customStyles.container?.heroImage || 'https://placehold.co/900x1200?text=Hero+Image',
          "Hero showcase",
          // No border-radius, no shadow padding — pure full-bleed
          "absolute inset-0 w-full h-full object-cover"
        )}

        {/* Subtle right-side fade so image blends into content on desktop */}
        <div className="hidden lg:block absolute inset-y-0 right-0 w-24 bg-gradient-to-r from-transparent to-white/10 pointer-events-none" />

        {/* Mobile bottom fade */}
        <div className="lg:hidden absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-white/80 to-transparent pointer-events-none" />
      </div>

      {/* ── RIGHT: Content panel ────────────────────────────────────────── */}
      <div
        className={`
          relative w-full lg:w-1/2 flex items-center justify-center
          px-8 sm:px-12 lg:px-16 xl:px-20
          py-14 lg:py-0
          ${customStyles.container?.background?.startsWith('bg-') ? customStyles.container.background : ''}
        `}
        style={
          !customStyles.container?.background?.startsWith('bg-') && customStyles.container?.background
            ? { background: customStyles.container.background }
            : {}
        }
      >
        <div className="w-full max-w-lg">

          {/* Badge */}
          {badge && (
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8 text-sm font-semibold tracking-wide"
              style={{
                backgroundColor: customStyles.container?.badgeBg || '#f3f4f6',
                color: customStyles.container?.badgeText || '#111827',
              }}
            >
              <Sparkles className="w-4 h-4" />
              <span>{badge}</span>
            </div>
          )}

          {/* Title */}
          <h1
            className="font-bold leading-[1.1] mb-6"
            style={{
              color: customStyles.container?.titleColor || '#111827',
              fontSize: 'clamp(2.2rem, 4vw, 3.5rem)',
            }}
          >
            {title}
          </h1>

          {/* Subtitle */}
          <p
            className="text-lg leading-relaxed mb-10 max-w-md"
            style={{ color: customStyles.container?.subtitleColor || '#4B5563' }}
          >
            {subtitle}
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 mb-14">
            <button
              onClick={() => ctaPrimary?.link && navigate(ctaPrimary.link)}
              className="group inline-flex items-center justify-center px-8 py-4 rounded-full font-semibold text-base shadow-lg transition-all hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0"
              style={{
                backgroundColor: customStyles.container?.ctaPrimaryBg || '#111827',
                color: customStyles.container?.ctaPrimaryText || '#ffffff',
              }}
            >
              {ctaPrimary?.text || "Shop Now"}
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>

            {ctaSecondary?.text && (
              <button
                onClick={() => ctaSecondary?.link && navigate(ctaSecondary.link)}
                className="inline-flex items-center justify-center px-8 py-4 rounded-full font-semibold text-base border-2 transition-all hover:-translate-y-0.5 active:translate-y-0"
                style={{
                  borderColor: customStyles.container?.ctaSecondaryBorder || '#6b7280',
                  color: customStyles.container?.ctaSecondaryText || '#374151',
                }}
              >
                <Play className="mr-2 w-4 h-4" />
                {ctaSecondary.text}
              </button>
            )}
          </div>

          {/* Stats — horizontal rule then 3-column grid */}
          {stats?.length > 0 && (
            <div
              className="border-t pt-10 grid gap-8"
              style={{
                borderColor: `${customStyles.container?.subtitleColor || '#4B5563'}22`,
                gridTemplateColumns: `repeat(${Math.min(stats.length, 3)}, 1fr)`,
              }}
            >
              {stats.map((stat, i) => (
                <div key={i}>
                  <div
                    className="text-3xl font-extrabold mb-1 tabular-nums"
                    style={{ color: customStyles.container?.titleColor || '#111827' }}
                  >
                    {stat.value}
                  </div>
                  <div
                    className="text-sm font-medium"
                    style={{ color: customStyles.container?.subtitleColor || '#6B7280' }}
                  >
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          )}

        </div>
      </div>
    </div>
  </div>
);

  // ─── CENTERED DARK ──────────────────────────────────────────────────────────
  const CenteredDark = () => (
    <div className={`relative overflow-hidden min-h-[600px] lg:min-h-[800px] flex items-center ${finalContainerClass}`} style={containerStyle}>
      {bgImage?.match(/\.(mp4|webm|mov)$/i) && (
        <video src={bgImage} autoPlay loop muted playsInline
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 text-center z-10 w-full">
        {badge && (
          <div
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full mb-8 border border-white/20 backdrop-blur-sm"
            style={{
              backgroundColor: customStyles.container?.badgeBg || 'rgba(255,255,255,0.15)',
              color: customStyles.container?.badgeText || '#ffffff',
            }}
          >
            <Sparkles className="w-5 h-5" />
            <span className="font-medium">{badge}</span>
          </div>
        )}
        <h1
          className="text-5xl md:text-6xl lg:text-7xl font-bold mb-8 leading-tight"
          style={{ color: customStyles.container?.titleColor || '#ffffff' }}
        >
          {title}
        </h1>
        <p
          className="text-xl md:text-2xl mb-12 max-w-3xl mx-auto"
          style={{ color: customStyles.container?.subtitleColor || 'rgba(255,255,255,0.9)' }}
        >
          {subtitle}
        </p>
        <div className="flex flex-col sm:flex-row gap-5 justify-center">
          {/* FIX: was missing inline-flex */}
          <button
            onClick={() => ctaPrimary?.link && navigate(ctaPrimary.link)}
            className="inline-flex items-center justify-center px-10 py-5 rounded-full font-bold text-lg shadow-xl transition-transform hover:scale-105"
            style={{
              backgroundColor: customStyles.container?.ctaPrimaryBg || '#ffffff',
              color: customStyles.container?.ctaPrimaryText || '#111827',
            }}
          >
            {ctaPrimary?.text || "Start Shopping"}
            <ArrowRight className="ml-3 w-6 h-6" />
          </button>
          {ctaSecondary?.text && (
            <button
              onClick={() => ctaSecondary?.link && navigate(ctaSecondary.link)}
              className="inline-flex items-center justify-center px-10 py-5 rounded-full font-bold text-lg border-2 border-white/40 backdrop-blur-sm transition-transform hover:scale-105 text-white"
            >
              {ctaSecondary.text}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // ─── IMAGE OVERLAY ──────────────────────────────────────────────────────────
  const ImageOverlay = () => (
    <div className={`relative min-h-[600px] lg:min-h-[800px] flex items-center ${finalContainerClass}`} style={containerStyle}>
      {bgImage && (
        bgImage.match(/\.(mp4|webm|mov)$/i) ? (
          <video src={bgImage} autoPlay loop muted playsInline
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div
            className="absolute inset-0"
            style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
          />
        )
      )}
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 z-10">
        <div className="max-w-3xl">
          {badge && (
            <div
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full mb-6 backdrop-blur-md border border-white/30 shadow-lg"
              style={{
                backgroundColor: customStyles.container?.badgeBg || 'rgba(255,255,255,0.9)',
                color: customStyles.container?.badgeText || '#111827',
              }}
            >
              <Sparkles className="w-5 h-5" />
              <span className="font-semibold">{badge}</span>
            </div>
          )}
          <h1
            className="text-5xl md:text-6xl lg:text-7xl font-extrabold mb-8 leading-tight"
            style={{ color: customStyles.container?.titleColor || '#ffffff' }}
          >
            {title}
          </h1>
          <p
            className="text-xl md:text-2xl mb-12 leading-relaxed"
            style={{ color: customStyles.container?.subtitleColor || 'rgba(255,255,255,0.9)' }}
          >
            {subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-5">
            <button
              onClick={() => ctaPrimary?.link && navigate(ctaPrimary.link)}
              className="inline-flex items-center justify-center px-10 py-5 rounded-full font-bold text-lg shadow-2xl transition-transform hover:scale-105"
              style={{
                backgroundColor: customStyles.container?.ctaPrimaryBg || '#ffffff',
                color: customStyles.container?.ctaPrimaryText || '#111827',
              }}
            >
              {ctaPrimary?.text || "Explore Now"}
              <ArrowRight className="ml-3 w-6 h-6" />
            </button>
            {ctaSecondary?.text && (
              <button
                onClick={() => ctaSecondary?.link && navigate(ctaSecondary.link)}
                className="inline-flex items-center justify-center px-10 py-5 rounded-full font-bold text-lg border-2 border-white/60 backdrop-blur-sm transition-transform hover:scale-105 text-white"
              >
                {ctaSecondary.text}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // ─── PRODUCT SHOWCASE ───────────────────────────────────────────────────────
  const ProductShowcase = () => (
    <div className={`relative overflow-hidden ${finalContainerClass}`} style={containerStyle}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="order-2 lg:order-1 relative">
            <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 rounded-3xl p-8 lg:p-12 aspect-square overflow-hidden shadow-2xl">
              {renderHeroMedia(
                product?.image || customStyles.container?.heroImage || "https://placehold.co/800x800?text=Featured+Product",
                product?.name || "Featured Product",
                "drop-shadow-2xl transition-transform hover:scale-105 duration-700"
              )}
              {badge && (
                <div
                  className="absolute top-6 left-6 px-5 py-2 rounded-full text-sm font-bold shadow-lg"
                  style={{
                    backgroundColor: customStyles.container?.badgeBg || '#dc2626',
                    color: customStyles.container?.badgeText || '#ffffff',
                  }}
                >
                  {badge}
                </div>
              )}
              {product?.rating && (
                <div className="absolute bottom-6 right-6 bg-white/95 backdrop-blur-sm rounded-2xl px-5 py-3 shadow-xl border border-gray-100">
                  <div className="flex items-center gap-2">
                    <Star className="w-6 h-6 fill-yellow-400 text-yellow-400" />
                    <span className="font-bold text-xl">{product.rating.toFixed(1)}</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{product.reviews || 0} reviews</p>
                </div>
              )}
            </div>
          </div>

          <div className="order-1 lg:order-2">
            {badge && (
              <div className="inline-flex items-center gap-2 bg-indigo-50 text-indigo-700 px-5 py-2 rounded-full mb-6">
                <TrendingUp className="w-5 h-5" />
                <span className="font-semibold text-sm">Featured</span>
              </div>
            )}
            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
              style={{ color: customStyles.container?.titleColor || '#111827' }}
            >
              {product?.name || title}
            </h1>
            <div className="flex items-baseline gap-4 mb-8">
              <span
                className="text-5xl md:text-6xl font-extrabold"
                style={{ color: customStyles.container?.ctaPrimaryBg || '#111827' }}
              >
                KES {(product?.price || 12999).toLocaleString()}
              </span>
              {product?.originalPrice && (
                <>
                  <span className="text-2xl text-gray-400 line-through">
                    KES {product.originalPrice.toLocaleString()}
                  </span>
                  <span className="bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-bold">
                    Save {Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)}%
                  </span>
                </>
              )}
            </div>
            <p
              className="text-lg md:text-xl mb-10 leading-relaxed"
              style={{ color: customStyles.container?.subtitleColor || '#4B5563' }}
            >
              {subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-5">
              <CartButton
                prod={product}
                primaryBg={customStyles.container?.ctaPrimaryBg}
                primaryText={customStyles.container?.ctaPrimaryText}
                label={ctaPrimary?.text || "Add to Cart"}
              />
              <button
                onClick={() => product?.id && navigate(`/products/${product.id}`)}
                className="inline-flex items-center justify-center px-10 py-5 rounded-full font-bold text-lg border-2 transition-transform hover:scale-105"
                style={{
                  borderColor: customStyles.container?.ctaSecondaryBorder || '#6b7280',
                  color: customStyles.container?.ctaSecondaryText || '#374151',
                }}
              >
                {ctaSecondary?.text || "View Details"}
                <ArrowRight className="ml-3 w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // ─── PRODUCT GRID ───────────────────────────────────────────────────────────
  // FIX: Each card now uses its own product from the `products` array
  const ProductGrid = () => {
    // Fallback: if no products array provided, show 3 placeholder cards
    const gridProducts = products && products.length > 0
      ? products
      : [1, 2, 3].map(i => ({
          id: null,
          name: `Featured Product ${i}`,
          price: 12999,
          originalPrice: null,
          image: customStyles.container?.heroImage || `https://placehold.co/600x600?text=Product+${i}`,
          rating: 4.9,
          reviews: 0,
        }));

    return (
      <div className={`relative overflow-hidden ${finalContainerClass}`} style={containerStyle}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 lg:py-20">
          <div className="text-center mb-16">
            {badge && (
              <div
                className="inline-flex items-center gap-2 px-5 py-2 rounded-full mb-6"
                style={{
                  backgroundColor: customStyles.container?.badgeBg || '#f3f4f6',
                  color: customStyles.container?.badgeText || '#111827',
                }}
              >
                <Package className="w-5 h-5" />
                <span className="font-medium">{badge}</span>
              </div>
            )}
            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
              style={{ color: customStyles.container?.titleColor || '#111827' }}
            >
              {title}
            </h1>
            <p
              className="text-xl md:text-2xl max-w-3xl mx-auto mb-10"
              style={{ color: customStyles.container?.subtitleColor || '#4B5563' }}
            >
              {subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-5 justify-center">
              <button
                onClick={() => ctaPrimary?.link && navigate(ctaPrimary.link)}
                className="inline-flex items-center justify-center px-10 py-5 rounded-full font-bold text-lg shadow-xl transition-transform hover:scale-105"
                style={{
                  backgroundColor: customStyles.container?.ctaPrimaryBg || '#111827',
                  color: customStyles.container?.ctaPrimaryText || '#ffffff',
                }}
              >
                <ShoppingBag className="mr-3 w-6 h-6" />
                {ctaPrimary?.text || "Shop Collection"}
                <ArrowRight className="ml-3 w-6 h-6" />
              </button>
              {ctaSecondary?.text && (
                <button
                  onClick={() => ctaSecondary?.link && navigate(ctaSecondary.link)}
                  className="inline-flex items-center justify-center px-10 py-5 rounded-full font-bold text-lg border-2 transition-transform hover:scale-105"
                  style={{
                    borderColor: customStyles.container?.ctaSecondaryBorder || '#6b7280',
                    color: customStyles.container?.ctaSecondaryText || '#374151',
                  }}
                >
                  {ctaSecondary.text}
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {gridProducts.map((prod, i) => (
              <div
                key={prod.id || i}
                className="group bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-100 cursor-pointer"
                onClick={() => prod.id && navigate(`/products/${prod.id}`)}
              >
                <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
                  {renderHeroMedia(
                    prod.image || `https://placehold.co/600x600?text=Product+${i + 1}`,
                    prod.name || `Product ${i + 1}`,
                    "group-hover:scale-110 transition-transform duration-700"
                  )}
                  {prod.originalPrice && prod.price && (
                    <div className="absolute top-4 right-4 bg-red-500 text-white px-4 py-1.5 rounded-full text-sm font-bold shadow-md">
                      -{Math.round(((prod.originalPrice - prod.price) / prod.originalPrice) * 100)}%
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="font-bold text-xl mb-3 line-clamp-2">
                    {prod.name || `Featured Product ${i + 1}`}
                  </h3>
                  {prod.rating && (
                    <div className="flex items-center gap-1 mb-4">
                      {[...Array(5)].map((_, idx) => (
                        <Star
                          key={idx}
                          className={`w-4 h-4 ${idx < Math.round(prod.rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-200'}`}
                        />
                      ))}
                      <span className="text-sm text-gray-600 ml-1">({prod.rating.toFixed(1)})</span>
                    </div>
                  )}
                  <div className="flex items-baseline gap-3 mb-6">
                    <span className="text-2xl font-extrabold text-gray-900">
                      KES {(prod.price || 0).toLocaleString()}
                    </span>
                    {prod.originalPrice && (
                      <span className="text-base text-gray-400 line-through">
                        KES {prod.originalPrice.toLocaleString()}
                      </span>
                    )}
                  </div>
                  {/* FIX: Each card now operates on its own `prod` object */}
                  <div onClick={(e) => e.stopPropagation()}>
                    <CartButton
                      prod={prod}
                      primaryBg={customStyles.container?.ctaPrimaryBg}
                      primaryText={customStyles.container?.ctaPrimaryText}
                      label={ctaPrimary?.text || "Add to Cart"}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={finalContainerClass} style={containerStyle}>
      {preset === 'split-layout' && <SplitLayout />}
      {preset === 'centered-dark' && <CenteredDark />}
      {preset === 'image-overlay' && <ImageOverlay />}
      {preset === 'product-showcase' && <ProductShowcase />}
      {preset === 'product-grid' && <ProductGrid />}
    </div>
  );
};

export default HeroSection;